import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, PencilSimple, Trash, Key, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const ALL_ROLES = [
  { value: "system_admin", label: "Admin Sistema" },
  { value: "editor", label: "Editor" },
  { value: "tech_support", label: "Soporte Técnico" },
  { value: "client_admin", label: "Admin Cliente" },
  { value: "standard_user", label: "Usuario Estándar" },
];

// Roles a client_admin is allowed to assign
const CLIENT_ADMIN_ASSIGNABLE = ["client_admin", "standard_user"];

const ROLE_COLORS = {
  system_admin: "bg-[#E63946] text-white",
  editor: "bg-[#002FA7] text-white",
  tech_support: "bg-amber-600 text-white",
  client_admin: "bg-[#2A9D8F] text-white",
  standard_user: "bg-secondary text-foreground",
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const isSystemAdmin = currentUser?.role === "system_admin";
  const isClientAdmin = currentUser?.role === "client_admin";
  const rolesForSelect = isSystemAdmin ? ALL_ROLES : ALL_ROLES.filter(r => CLIENT_ADMIN_ASSIGNABLE.includes(r.value));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "standard_user", is_active: true });
  const [saving, setSaving] = useState(false);
  const [pwDialog, setPwDialog] = useState({ open: false, userId: null, userEmail: "" });
  const [newPw, setNewPw] = useState("");

  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data.users);
    } catch (err) {
      if (err.response?.status === 403) toast.error("No tienes permiso para ver usuarios");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", email: "", password: "", role: isClientAdmin ? "standard_user" : "standard_user", is_active: true });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u.id);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, is_active: u.is_active !== false });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        const update = { name: form.name, role: form.role, is_active: form.is_active };
        if (form.email) update.email = form.email;
        await api.put(`/users/${editing}`, update);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", { name: form.name, email: form.email, password: form.password, role: form.role });
        toast.success("Usuario creado");
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  const openPwDialog = (u) => {
    setPwDialog({ open: true, userId: u.id, userEmail: u.email });
    setNewPw("");
  };

  const handleSavePassword = async () => {
    if (newPw.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    try {
      await api.post(`/users/${pwDialog.userId}/change-password`, { new_password: newPw });
      toast.success("Contraseña actualizada");
      setPwDialog({ open: false, userId: null, userEmail: "" });
      setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar contraseña");
    }
  };

  const handleResetPassword = async (u) => {
    if (!window.confirm(`¿Enviar contraseña temporal a ${u.email}? El usuario recibirá un correo y deberá cambiarla al iniciar sesión.`)) return;
    try {
      const res = await api.post(`/users/${u.id}/reset-password`);
      toast.success("Contraseña temporal enviada", { description: `Email enviado a ${res.data.email_sent_to}` });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al restablecer contraseña");
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("es-GT", { year: "numeric", month: "short", day: "numeric" }); }
    catch { return d; }
  };

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Gestión de Usuarios</h1>
          <p className="text-muted-foreground text-sm mt-1">Administra usuarios y permisos de la plataforma</p>
        </div>
        <Button data-testid="add-user-button" className="rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white" onClick={openNew}>
          <Plus size={16} className="mr-2" /> Nuevo Usuario
        </Button>
      </div>

      <Card className="rounded-none shadow-none border border-border/40" data-testid="users-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-mono text-xs uppercase tracking-wider">Nombre</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">Email</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">Rol</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">Estado</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">Creado</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No hay usuarios</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id} className="hover:bg-secondary/30 transition-colors duration-150" data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold rounded-none flex-shrink-0">
                        {u.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <span className="font-medium text-sm">{u.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <Badge className={`rounded-none text-[10px] font-mono ${ROLE_COLORS[u.role] || ROLE_COLORS.standard_user}`}>
                      {ALL_ROLES.find(r => r.value === u.role)?.label || u.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-block w-2 h-2 rounded-full ${u.is_active !== false ? "bg-[#2A9D8F]" : "bg-[#E63946]"}`} />
                    <span className="ml-2 text-xs">{u.is_active !== false ? "Activo" : "Inactivo"}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isSystemAdmin && (
                        <>
                          <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" title="Cambiar contraseña"
                            onClick={() => openPwDialog(u)} data-testid={`change-pw-${u.id}`}>
                            <Key size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" title="Restablecer contraseña (envía email)"
                            onClick={() => handleResetPassword(u)} data-testid={`reset-pw-${u.id}`}>
                            <ArrowClockwise size={14} />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                      {u.id !== currentUser?.id && (
                        <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}>
                          <Trash size={14} className="text-[#E63946]" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editing ? "Editar Usuario" : "Nuevo Usuario"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Nombre</Label>
              <Input data-testid="user-name-input" className="rounded-none h-10 bg-card" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Email</Label>
              <Input data-testid="user-email-input" type="email" className="rounded-none h-10 bg-card" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!!editing} />
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase text-muted-foreground">Contraseña</Label>
                <Input data-testid="user-password-input" type="password" className="rounded-none h-10 bg-card" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Rol</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-none h-10 bg-card" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {rolesForSelect.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="user-active-toggle" />
                <Label className="text-sm">{form.is_active ? "Activo" : "Inactivo"}</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button data-testid="save-user-button" className="rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white" onClick={handleSave} disabled={saving || !form.name || !form.email || (!editing && !form.password)}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password change dialog (admin action) */}
      <Dialog open={pwDialog.open} onOpenChange={(v) => setPwDialog({ ...pwDialog, open: v })}>
        <DialogContent className="rounded-none max-w-md" data-testid="admin-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Cambiar contraseña</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Usuario: <strong>{pwDialog.userEmail}</strong>
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase">Nueva contraseña</Label>
              <Input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="rounded-none"
                placeholder="Mínimo 8 caracteres"
                minLength={8}
                data-testid="admin-pw-input"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Al confirmar, el usuario podrá iniciar sesión con esta contraseña inmediatamente.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setPwDialog({ open: false, userId: null, userEmail: "" })}>
              Cancelar
            </Button>
            <Button className="rounded-none bg-[#002FA7] hover:bg-[#001D66]" onClick={handleSavePassword} data-testid="admin-pw-save">
              Cambiar contraseña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
